import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Mail, CreditCard, CheckCircle, XCircle, DollarSign, Edit2, Clock, Heart, ChevronDown, Users } from 'lucide-react';

const emailTemplates = [
  { id: 'new_booking', label: 'Booking Confirmation', icon: Mail },
  { id: 'payment_reminder', label: 'Payment Reminder', icon: CreditCard },
  { id: 'booking_approved', label: 'Approval Email', icon: CheckCircle },
  { id: 'booking_rejected', label: 'Rejection Email', icon: XCircle },
  { id: 'payment_confirmed', label: 'Payment Confirmed', icon: DollarSign },
  { id: 'booking_updated', label: 'Booking Updated', icon: Edit2 },
  { id: 'student_class_details', label: 'Class Details', icon: Mail },
  { id: 'class_reminder', label: '24hr Class Reminder', icon: Clock },
  { id: 'class_followup', label: 'Post-Class Thank You', icon: Heart },
];

// Individual student email button - used when student is alone or for per-student actions
export const StudentEmailButton = ({ bookingId, student, onResend, size = 'icon' }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={size}
          className="h-7 w-7 p-0 text-white hover:text-blue-300 hover:bg-blue-900/30"
          data-testid={`resend-email-student-${bookingId}-${student.email}`}
          title={`Send email to ${student.first_name}`}
        >
          <Mail className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
        <DropdownMenuLabel className="text-white text-xs">
          Send to: {student.first_name} {student.last_name}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-700" />
        {emailTemplates.map((template) => {
          const Icon = template.icon;
          return (
            <DropdownMenuItem
              key={template.id}
              onClick={() => onResend(bookingId, template.id, student.email)}
              className="cursor-pointer text-slate-200 hover:bg-slate-700 focus:bg-slate-700"
              data-testid={`resend-${template.id}-${bookingId}-${student.email}`}
            >
              <Icon className="h-4 w-4 mr-2 text-white" />
              {template.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Group email button - shown once for bookings with multiple students
export const GroupEmailButton = ({ booking, onResend }) => {
  const students = booking?.students || [];
  
  if (students.length === 0) return null;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-blue-700 text-white hover:bg-blue-900/30 rounded-full"
          data-testid={`resend-email-group-${booking.id}`}
        >
          <Users className="h-3 w-3 mr-2" />
          Email All ({students.length})
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
        <DropdownMenuLabel className="text-white text-xs">
          Send to all {students.length} students
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-700" />
        {emailTemplates.map((template) => {
          const Icon = template.icon;
          return (
            <DropdownMenuItem
              key={template.id}
              onClick={() => onResend(booking.id, template.id, null)}
              className="cursor-pointer text-slate-200 hover:bg-slate-700 focus:bg-slate-700"
              data-testid={`resend-group-${template.id}-${booking.id}`}
            >
              <Icon className="h-4 w-4 mr-2 text-white" />
              {template.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default { StudentEmailButton, GroupEmailButton };
